import SyncIcon from '@mui/icons-material/Sync';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React from 'react';

import { CREATE, DataAPIContext, GET_ONE } from '../api';
import { ContextStore } from '../store/contextStore';
import { isOk, serverErrorMessage } from '../utils/errors';
import { operatorDefaultFlagColumns } from './TagCategoryTableView';

/**
 * "Sync with live" (sdtc WP-E, backend plan 2026-10-29-sandbox-default-tag-categories-sync): on a sandbox's Trip
 * defaults, make the sandbox's default tag categories match its production operator's.
 *
 * - `GET  /v2/edit/tour_operator/{id}/default_tag_categories/production_sync/` -- the preview:
 *   `{added: [{code, name, flags}], changed: {code: {field: [sandbox, live]}}, removed: [code],
 *   categories_created: [code], token}`.
 * - `POST` the same path with `{token}` -- applies that preview; 409 `production_sync_stale` when the sandbox's or
 *   live's defaults changed since it (nothing written).
 * - Both need `tour_operator.write`: a 403 carries the reason in `detail` (an admin waiting in no access group is told
 *   so), and is an answer here, not an expired session (`keepSessionOn403`). The operator is named in the URL, so the
 *   franchise wrapper adds nothing (`explicitTenant`). 404 unless the operator is a sandbox of a production operator.
 */

const PRODUCTION_SYNC = 'default_tag_categories/production_sync/';
const CALL_OPTIONS = { keepSessionOn403: true, explicitTenant: true };

export const fetchProductionSyncPreview = (dataAPI, operatorId) =>
  dataAPI(GET_ONE, 'tour_operator', { id: operatorId, urlSuffix: PRODUCTION_SYNC, ...CALL_OPTIONS });

export const applyProductionSync = (dataAPI, operatorId, token) =>
  dataAPI(CREATE, 'tour_operator', { urlSuffix: `${operatorId}/${PRODUCTION_SYNC}`, data: { token }, ...CALL_OPTIONS });

export const DIRTY_MESSAGE = 'Save or discard your changes first';
export const IN_SYNC_MESSAGE = 'Already in sync with live';
export const STALE_MESSAGE = 'Live or sandbox changed since the preview';
export const SYNCED_MESSAGE = 'Default tag categories synced with live';
export const RELOAD_FAILED_MESSAGE = 'Synced, but reloading failed — reload the page';
const NETWORK_MESSAGE = "Couldn't reach the server. Try again.";

// The grid's own column labels (TagCategoryTableView), so the preview speaks the table's words; the preview's
// `changed` also names the row's order and tag limits.
const FIELD_LABELS = {
  ...Object.fromEntries(operatorDefaultFlagColumns.map((col) => [col.id, col.label])),
  sequence: 'Order',
  min_tags: 'Minimum tags',
  max_tags: 'Maximum tags',
};
const fieldLabel = (field) => FIELD_LABELS[field] || field;
const formatValue = (value) => {
  if (value === true) return 'on';
  if (value === false) return 'off';
  if (value === null || value === undefined) return 'none';
  return String(value);
};

export const isPreviewEmpty = (preview) =>
  !preview.added?.length &&
  !Object.keys(preview.changed || {}).length &&
  !preview.removed?.length &&
  !preview.categories_created?.length;

// A 403 in the backend's words (`detail`, which names why); anything else as the rest of the page words a refusal.
const refusalMessage = (res, fallback) =>
  res?.statusCode === 403 ? res.data?.detail || res.data?.message || fallback : serverErrorMessage(res, fallback);

const isPreview = (data) => !!data && typeof data === 'object' && !Array.isArray(data) && typeof data.token === 'string';

const Group = ({ title, count, testId, children }) => (
  <Box data-testid={testId} sx={{ mb: 2 }}>
    <Typography fontWeight={500} gutterBottom>
      {title} ({count})
    </Typography>
    {children}
  </Box>
);

Group.propTypes = {
  title: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  testId: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const PreviewGroups = ({ preview, nameOf }) => {
  const changed = Object.entries(preview.changed || {});
  return (
    <>
      {preview.added?.length > 0 && (
        <Group title="To be added" count={preview.added.length} testId="sync-added">
          {preview.added.map((row) => {
            const on = Object.keys(row.flags || {}).filter((flag) => row.flags[flag] === true);
            return (
              <Box key={row.code} sx={{ mb: 1 }}>
                <Typography variant="body2">{nameOf(row.code)}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {on.length ? (
                    on.map((flag) => <Chip key={flag} size="small" label={fieldLabel(flag)} title={flag} />)
                  ) : (
                    <Typography variant="caption" color="text.secondary">All flags off</Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Group>
      )}

      {changed.length > 0 && (
        <Group title="To be changed" count={changed.length} testId="sync-changed">
          {changed.map(([code, fields]) => (
            <Box key={code} sx={{ mb: 1 }}>
              <Typography variant="body2">{nameOf(code)}</Typography>
              {Object.entries(fields || {}).map(([field, values]) => {
                const [sandbox, live] = Array.isArray(values) ? values : [undefined, undefined];
                return (
                  <Typography key={field} variant="caption" component="div" color="text.secondary" sx={{ pl: 2 }} title={field}>
                    {`${fieldLabel(field)}: ${formatValue(sandbox)} → ${formatValue(live)}`}
                  </Typography>
                );
              })}
            </Box>
          ))}
        </Group>
      )}

      {preview.removed?.length > 0 && (
        <Alert severity="warning" data-testid="sync-removed" sx={{ mb: 2 }}>
          <AlertTitle>{`To be removed (${preview.removed.length})`}</AlertTitle>
          <Typography variant="body2" gutterBottom>
            Not in live&apos;s defaults, so removed from this sandbox&apos;s:
          </Typography>
          {preview.removed.map((code) => (
            <Typography key={code} variant="body2" component="div">{nameOf(code)}</Typography>
          ))}
        </Alert>
      )}

      {preview.categories_created?.length > 0 && (
        <Group title="Categories to be created" count={preview.categories_created.length} testId="sync-created">
          <Typography variant="caption" color="text.secondary" component="div" gutterBottom>
            These tag categories are not in the sandbox yet. They are copied from live.
          </Typography>
          {preview.categories_created.map((code) => (
            <Typography key={code} variant="body2" component="div">{nameOf(code)}</Typography>
          ))}
        </Group>
      )}
    </>
  );
};

PreviewGroups.propTypes = {
  preview: PropTypes.object.isRequired,
  nameOf: PropTypes.func.isRequired,
};

const SyncWithLiveButton = ({ operatorId, formHasChanges = false, onApplied, categories = [] }) => {
  const dataAPI = React.useContext(DataAPIContext);
  const { environment, pairedEnvironment } = React.useContext(ContextStore);
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = React.useState(false);
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [applying, setApplying] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [notice, setNotice] = React.useState(null);
  // Applied, but the refetch after it failed: the form still shows the rows from before the sync.
  const [reloadFailed, setReloadFailed] = React.useState(false);
  // Only the latest preview request may set state: a reopened dialog, or the re-preview after a 409, wins.
  const requestRef = React.useRef(0);

  const live = pairedEnvironment && pairedEnvironment.environment === 'production' ? pairedEnvironment : null;
  const label = live?.name ? `Sync with live (${live.name})` : 'Sync with live';

  // Names for the codes the preview lists only by code: the sandbox's own rows, then the rows to add.
  const names = React.useMemo(() => {
    const map = {};
    (categories || []).forEach((row) => {
      if (row?.category?.code && row.category.name) map[row.category.code] = row.category.name;
    });
    (preview?.added || []).forEach((row) => {
      if (row.code && row.name) map[row.code] = row.name;
    });
    return map;
  }, [categories, preview]);
  const nameOf = React.useCallback((code) => (names[code] ? `${names[code]} (${code})` : code), [names]);

  const loadPreview = React.useCallback(async () => {
    const request = ++requestRef.current;
    setLoading(true);
    setError(null);
    setPreview(null);
    let res;
    try {
      res = await fetchProductionSyncPreview(dataAPI, operatorId);
    } catch (e) {
      res = null;
    }
    if (request !== requestRef.current) return;
    if (res && isOk(res) && isPreview(res.data)) {
      setPreview(res.data);
    } else {
      setError(res ? refusalMessage(res, "Couldn't load the changes from live") : NETWORK_MESSAGE);
    }
    setLoading(false);
  }, [dataAPI, operatorId]);

  if (environment !== 'sandbox') {
    return null;
  }

  const onOpen = () => {
    setOpen(true);
    setNotice(null);
    setReloadFailed(false);
    loadPreview();
  };

  const onClose = () => {
    requestRef.current += 1; // drop a preview still on its way
    setOpen(false);
    setLoading(false);
  };

  const onApply = async () => {
    setApplying(true);
    setError(null);
    setNotice(null);
    try {
      const res = await applyProductionSync(dataAPI, operatorId, preview.token);
      if (isOk(res)) {
        // The refetch replaces the form's loaded snapshot with the new server state. When it answers false or
        // fails, the form still shows the rows from before the sync: say so instead of "synced". The preview
        // is spent either way.
        let reloaded;
        try {
          reloaded = (await onApplied?.()) !== false;
        } catch (e) {
          reloaded = false;
        }
        if (!reloaded) {
          setPreview(null);
          setReloadFailed(true);
          return;
        }
        enqueueSnackbar(SYNCED_MESSAGE, { variant: 'success' });
        setOpen(false);
        return;
      }
      if (res?.statusCode === 409) {
        setNotice(res.data?.code === 'production_sync_stale' ? STALE_MESSAGE : serverErrorMessage(res, STALE_MESSAGE));
        await loadPreview();
        return;
      }
      setError(refusalMessage(res, "Couldn't sync with live"));
    } catch (e) {
      setError(NETWORK_MESSAGE);
    } finally {
      setApplying(false);
    }
  };

  const canApply = !!preview && !isPreviewEmpty(preview);

  return (
    <>
      <Tooltip title={formHasChanges ? DIRTY_MESSAGE : `Make this sandbox's default tag categories match ${live?.name ? `${live.name} (live)` : 'live'}`}>
        <span>
          <Button variant="outlined" size="small" startIcon={<SyncIcon />} disabled={formHasChanges} onClick={onOpen}>
            {label}
          </Button>
        </span>
      </Tooltip>

      <Dialog open={open} onClose={applying ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="sync-with-live-title">
        <DialogTitle id="sync-with-live-title">{label}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The sandbox&apos;s default tag categories will match live&apos;s. Nothing changes on live.
          </Typography>
          {notice && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {notice}. Review the changes again.
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {reloadFailed && (
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="sync-reload-failed">
              {RELOAD_FAILED_MESSAGE}
            </Alert>
          )}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} aria-label="Loading the changes" />
            </Box>
          )}
          {!loading && preview && (isPreviewEmpty(preview)
            ? <Typography>{IN_SYNC_MESSAGE}</Typography>
            : <PreviewGroups preview={preview} nameOf={nameOf} />)}
        </DialogContent>
        <DialogActions>
          {!loading && !preview && error && (
            <Button onClick={loadPreview}>Try again</Button>
          )}
          <Button onClick={onClose} disabled={applying}>
            {canApply ? 'Cancel' : 'Close'}
          </Button>
          {canApply && (
            <Button variant="contained" onClick={onApply} disabled={applying || loading} startIcon={applying ? <CircularProgress size={16} /> : null}>
              Sync
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

SyncWithLiveButton.propTypes = {
  /** The sandbox operator to sync: the session's own (the backend answers 404 for any other). */
  operatorId: PropTypes.number.isRequired,
  /** The page has unsaved changes: the apply refetches the operator, which would drop them. */
  formHasChanges: PropTypes.bool,
  /** Refetches the operator after an apply; may return a promise (the dialog closes when it settles). Answering
   *  false, or failing, means the form was not reloaded: the dialog says to reload the page. */
  onApplied: PropTypes.func,
  /** The sandbox's current default rows, to name the codes the preview lists. */
  categories: PropTypes.array,
};

export default SyncWithLiveButton;
